// How many videos — ONE place that knows.
//
// The recap under the editor, the "Done editing · N videos" button, each queue row's badge, each exported card's badge, the
// queue footer and the export strip all read from here (videoBreakdown → clipVideoCount → studioSummary). Nothing else in the
// Studio does any counting, so the numbers cannot drift apart (the Scan once had three definitions of "needs a feature").
//
// A clip's total = 1 original + scripts (× voices × titles × hooks) + concepts. This is exactly the arithmetic generate() runs
// (App.js countVersions delegates to videoBreakdown), so the number on screen is the number of files that come out.

// input: { scripts: [{ key, voices }] | null,   // null = no scripts: one title-only variant. `voices` = how many voice variants that script gets
//          titleMult, hookMult,                  // >=1
//          concepts: ['ASMR', ...] }             // concepts that will actually render
export function videoBreakdown({ scripts, titleMult = 1, hookMult = 1, concepts = [] }) {
  const tm = Math.max(1, titleMult), hm = Math.max(1, hookMult);
  let scriptsN, scriptsDetail;
  if (scripts == null) {                                  // no scripts: one title-only variant, still multiplied by titles and hooks like any script
    scriptsN = tm * hm;
    scriptsDetail = 'title only' + (tm > 1 ? ` × ${tm} titles` : '') + (hm > 1 ? ` × ${hm} hooks` : '');
  } else {
    scriptsN = scripts.reduce((n, s) => n + Math.max(1, s.voices) * tm * hm, 0);
    const v = scripts.length ? scripts[0].voices : 0;
    const same = scripts.every(s => s.voices === v);
    scriptsDetail = !scripts.length ? 'none'
      : (same ? `${scripts.length} × ${Math.max(1, v)} voice${Math.max(1, v) > 1 ? 's' : ''}` : `${scripts.length} script${scripts.length > 1 ? 's' : ''}`)
        + (tm > 1 ? ` × ${tm} titles` : '') + (hm > 1 ? ` × ${hm} hooks` : '');
  }
  const conceptsN = concepts.length;
  return {
    original: { n: 1, detail: 'source + titling' },
    scripts: { n: scriptsN, detail: scriptsDetail },
    concepts: { n: conceptsN, detail: conceptsN ? concepts.join(' · ') : 'none' },
    variants: scriptsN + conceptsN,                       // everything except the original
    total: 1 + scriptsN + conceptsN,
  };
}

// A clip taking more than this is where the taxonomy nudge appears (a reminder, never a block).
export const VIDEOS_PER_CLIP_NUDGE = 5;
export const overNudge = (total) => total > VIDEOS_PER_CLIP_NUDGE;

const IN_BAR = ['exporting', 'exported', 'partial', 'failed'];

// Videos that really came out of a run: the original (always there, pass-through or titled) + the variants that are out.
// The titled original is the original itself, so it is not counted twice.
export const videosOut = (run) => (run ? run.okKeys.filter(k => !String(k).endsWith('|orig-title')).length + 1 : 0);

// The count and the tone of ONE clip, wherever it is in its life:
//   uploading / ready-edit  → what the settings in the panel would produce (grey)
//   ready-export            → what it was marked done with (orange)
//   exporting               → what it is planned to produce (orange)
//   exported / partial      → what actually came out: 1 original + the variants that are out (green / amber)
//   failed                  → 0 (red): nothing came out
export function clipVideoCount(item, liveTotal, job) {
  const planned = item.counts ? item.counts.total : liveTotal;
  switch (item.studio) {
    case 'ready-export': return { count: planned, tone: 'orange' };
    case 'exporting':    return { count: planned, tone: 'orange' };
    case 'exported':     return { count: job && job.run ? videosOut(job.run) : planned, tone: 'green' };
    case 'partial':      return { count: job && job.run ? videosOut(job.run) : planned, tone: 'amber' };
    case 'failed':       return { count: 0, tone: 'red' };
    default:             return { count: liveTotal, tone: 'grey' };
  }
}

// Everything the Studio shows about counts, from one pass over the clips.
//   items: queue items ({ id, studio, counts? }) · liveTotal: total of the settings currently in the panel
//   jobs : export jobs by clip id ({ run, done, status }) · runIds: the clips of the current "Export all" run
export function studioSummary({ items, liveTotal, jobs = {}, runIds = [] }) {
  const perClip = {};
  let clipsInQueue = 0, videosInQueue = 0, clipsExported = 0, videosExported = 0;
  items.filter(Boolean).forEach((it) => {
    const c = clipVideoCount(it, liveTotal, jobs[it.id]);
    perClip[it.id] = c;
    if (IN_BAR.includes(it.studio)) {
      if (it.studio === 'exported' || it.studio === 'partial') { clipsExported++; videosExported += c.count; }
    } else { clipsInQueue++; videosInQueue += c.count; }
  });
  // Progress of the run, in videos (not clips): finished clips count what came out, the clip being rendered what is out so far.
  let totalVideos = 0, doneVideos = 0, running = false;
  runIds.forEach((id) => {
    const it = items.find(v => v && v.id === id), job = jobs[id];
    if (!it) return;
    const planned = it.counts ? it.counts.total : (perClip[id] ? perClip[id].count : 0);
    totalVideos += planned;
    if (job && (job.status === 'ready' || job.status === 'error')) doneVideos += planned;                  // finished: everything it was meant to produce has been attempted
    else if (job && job.status === 'rendering') { running = true; doneVideos += Math.min(planned, 1 + (job.done || 0) + (job.err || 0)); }
    else if (job && job.status === 'queued') running = true;
  });
  return {
    perClip,
    clipsInQueue, videosInQueue, clipsExported, videosExported,
    footer: `${clipsInQueue} in queue · ${videosInQueue} videos · ${clipsExported} exported`,
    run: { running, totalVideos, doneVideos: Math.min(doneVideos, totalVideos) },
  };
}
